import cx from 'classnames';
import React, {Fragment} from 'react';

import {FontVariant} from '../../../../design-tokens/constants';
import {ThemeProvider} from '../../../themes/ThemeContext';
import type {ColorScheme, TextVariant} from '../../../types/variants';
import {Box} from '../../elements/Box/Box';
import {Divider} from '../../elements/Divider/Divider';
import {Icon} from '../../elements/Icon/Icon';
import {ListLink} from '../../elements/ListLink/ListLink';
import {Text} from '../../elements/Text/Text';
import {List} from '../List/List';
import {Row} from '../List/Row';

import type {MenuContent, MenuItem, TitledMenuContent} from './types';

import styles from './Menu.module.scss';

export const PlainMenuEntry: React.FC<
    React.ComponentProps<typeof Row> & {
        fontVariant?: FontVariant;
        item: MenuItem;
        variant?: TextVariant;
    }
> = ({fontVariant, item, variant, ...props}) => (
    <Row
        aria-checked={item.isAriaChecked}
        aria-selected={item.isAriaSelected}
        role={item.role}
        {...props}
    >
        <Text className="px-1" fontVariant={fontVariant} variant={variant}>
            {item.label}
        </Text>
        {item.icon && (
            <Icon
                color={item.iconColor}
                size={item.iconSize ?? 'compact'}
                source={item.icon}
            />
        )}
    </Row>
);

export const MenuEntry: React.FC<{item: MenuItem}> = ({item}) => {
    const hasWrapper = item.linkWrapper;

    if (item.url || item.clickAction || item.isDisabled) {
        return (
            <ListLink
                aria-checked={item.isAriaChecked}
                aria-label={
                    !hasWrapper && item.target === '_blank'
                        ? `${item.label} (Opens in a new tab)`
                        : undefined
                }
                aria-selected={item.isAriaSelected}
                data-testid={item.testId}
                href={!hasWrapper ? item.url : undefined}
                icon={item.icon}
                iconAlignedStart={item.iconAlignedStart}
                iconColor={item.iconColor}
                iconSize={item.iconSize}
                isDisabled={item.isDisabled}
                isFixedIcon={item.isFixedIcon}
                onClick={item.clickAction}
                role={item.role}
                target={!hasWrapper ? item.target : undefined}
                variant={item.variant ?? 'inherit'}
            >
                {item.label}
            </ListLink>
        );
    }

    return <PlainMenuEntry item={item} />;
};

const sectionKey = (section: MenuItem[]) =>
    section.map(item => String(item.label)).join(':');

const DefaultWrapper: React.FC<
    React.PropsWithChildren<{
        className?: string;
        colorScheme?: ColorScheme;
        testId?: string;
    }>
> = ({children, className, colorScheme = 'light', testId}) => (
    <ThemeProvider colorScheme={colorScheme}>
        <Box data-testid={testId} className={cx(styles.menu, className)}>
            {children}
        </Box>
    </ThemeProvider>
);

export const Menu: React.FC<{
    className?: string;
    colorScheme?: ColorScheme;
    isAriaMultiSelectable?: boolean;
    menuContent: MenuContent | TitledMenuContent;
    menuEntryComponent?: typeof MenuEntry;
    onPointerDownCapture?:
        React.PointerEventHandler<HTMLDivElement> | undefined;
    role?: React.AriaRole;
    tabIndex?: number;
    testId?: string;
    wrapperComponent?: typeof DefaultWrapper;
}> = ({
    className,
    colorScheme = 'light',
    isAriaMultiSelectable,
    menuContent,
    menuEntryComponent: MenuEntryComponent = MenuEntry,
    onPointerDownCapture,
    role = 'menu',
    tabIndex,
    testId,
    wrapperComponent: Wrapper = DefaultWrapper,
}) => {
    const hasSectionTitle = !Array.isArray(menuContent);
    const content = Object.entries(menuContent);

    return (
        <Wrapper
            className={className}
            colorScheme={colorScheme}
            testId={testId}
        >
            {content.map(([title, section], i) => {
                const lastSection = content.length - 1 === i;
                return (
                    <Fragment key={sectionKey(section)}>
                        {hasSectionTitle && (
                            <>
                                <div className="p-2">
                                    <Text fontVariant={FontVariant.BodyBold}>
                                        {title}
                                    </Text>
                                </div>
                                <div className="px-2">
                                    <Divider role="separator" />
                                </div>
                            </>
                        )}
                        <List
                            onPointerDownCapture={onPointerDownCapture}
                            aria-multiselectable={isAriaMultiSelectable}
                            className="p-1"
                            role={role}
                            spacing="compact"
                            tabIndex={tabIndex}
                        >
                            {section.map((item, index) => {
                                const LinkWrapper = item.linkWrapper;

                                return LinkWrapper ? (
                                    <LinkWrapper
                                        key={`${item.label}-wrapper-${index}`}
                                    >
                                        <MenuEntryComponent item={item} />
                                    </LinkWrapper>
                                ) : (
                                    <MenuEntryComponent
                                        key={`${item.label}-entry-${index}`}
                                        item={item}
                                    />
                                );
                            })}
                        </List>
                        {!lastSection && (
                            <div className="px-2">
                                <Divider role="separator" />
                            </div>
                        )}
                    </Fragment>
                );
            })}
        </Wrapper>
    );
};
